"use client"

import { useEffect, useId, useMemo, useRef, useState } from "react"
import { useField } from "@payloadcms/ui"
import type { RelationshipFieldClientComponent } from "payload"
import { Check, ChevronsUpDown, Search, X, Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Relationship field. Single-value and `hasMany`
 * variants both go through the same Combobox pattern:
 *
 *   Popover trigger button
 *     → Command palette with async search
 *       → Calls Payload's REST `/api/<collection>` with `where[like]`
 *         query params scoped to the related collection's
 *         `useAsTitle` field.
 *
 * The value stored in the form matches Payload's native format:
 *   • single, one target      → the document id (string | number)
 *   • hasMany                 → array of ids
 *   • polymorphic `relationTo: string[]` → { relationTo, value }
 *
 * Polymorphic multi-collection relationships are rare in this project
 * (none currently), but we keep the wire format compatible so a future
 * field config doesn't have to work around us.
 *
 * Filter / permission rules:
 *   • `filterOptions` set on the field is NOT yet honoured client-side;
 *     it's a Payload-side function. The list render still calls the
 *     REST endpoint which applies server-side access control, so
 *     whatever the user isn't allowed to see doesn't appear.
 *   • Values already stored on the doc are preserved even when they
 *     fall outside the current filter (otherwise editors couldn't
 *     clear a now-invalid value).
 */

type RelationTarget = string | { relationTo: string; value: string | number }
type FieldValue = RelationTarget | RelationTarget[] | null | undefined

type FoundDoc = {
  id: string | number
  /** Resolved display label — uses the collection's `useAsTitle` field. */
  title: string
  /** Slug of the collection this doc lives in (matters for polymorphic). */
  relationTo: string
}

/** Resolve a Payload `useAsTitle` value from an arbitrary doc object. */
function extractTitle(doc: Record<string, unknown>): string {
  const candidates = ["name", "title", "label", "email", "slug", "id"]
  for (const k of candidates) {
    const v = doc[k]
    if (typeof v === "string" && v.trim()) return v
  }
  return String(doc.id ?? "—")
}

export const ShadcnRelationshipField: RelationshipFieldClientComponent = (
  props,
) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<FieldValue>({
    path: path ?? fieldName,
  })
  const triggerId = useId()

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const required = Boolean((field as { required?: boolean }).required)
  const hasMany = Boolean((field as { hasMany?: boolean }).hasMany)
  const relationTo = (field as { relationTo?: string | string[] }).relationTo
  // We only support single-collection relationships in shadcn for now.
  // Polymorphic (array of collections) would require a discriminator in
  // the dropdown — easy to add later; for now fall back to Payload.
  const relationSlug = Array.isArray(relationTo) ? relationTo[0] : relationTo

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<FoundDoc[]>([])
  const [loading, setLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  // Current selection(s) stored in Payload form state. We hydrate a
  // display map of id → title so we can render the selected chip/label
  // without refetching on every render.
  const [titles, setTitles] = useState<Map<string | number, string>>(
    new Map(),
  )

  const selectedIds = useMemo<Array<string | number>>(() => {
    if (value == null) return []
    const arr = hasMany ? (Array.isArray(value) ? value : []) : [value]
    return arr
      .map((v) =>
        typeof v === "string" || typeof v === "number"
          ? v
          : (v as { value?: string | number }).value,
      )
      .filter((v): v is string | number => v != null)
  }, [value, hasMany])

  // Fetch the titles for any preselected ids (on first mount / when the
  // doc is re-opened).
  useEffect(() => {
    if (!relationSlug) return
    const missing = selectedIds.filter((id) => !titles.has(id))
    if (missing.length === 0) return

    let cancelled = false
    const fetchTitles = async () => {
      const next = new Map(titles)
      await Promise.all(
        missing.map(async (id) => {
          try {
            const res = await fetch(
              `/api/${relationSlug}/${encodeURIComponent(String(id))}?depth=0`,
              { credentials: "include" },
            )
            if (!res.ok) return
            const doc = (await res.json()) as Record<string, unknown>
            next.set(id, extractTitle(doc))
          } catch {
            /* silent */
          }
        }),
      )
      if (!cancelled) setTitles(next)
    }
    void fetchTitles()
    return () => {
      cancelled = true
    }
    // We deliberately depend on the stringified id list so Maps don't
    // retrigger infinite loops on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationSlug, selectedIds.join(",")])

  // Debounced search query against `/api/<collection>` whenever the
  // popover is open.
  useEffect(() => {
    if (!open || !relationSlug) return

    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          depth: "0",
          limit: "30",
          sort: "-createdAt",
        })
        // Loose LIKE search on the most common title fields. Payload
        // 'like' query on a non-existing field is a noop (not an error),
        // so the OR chain is safe even when the collection doesn't have
        // every candidate.
        if (query.trim()) {
          for (const [idx, f] of ["name", "title", "email", "slug"].entries()) {
            params.append(`where[or][${idx}][${f}][like]`, query.trim())
          }
        }
        const res = await fetch(
          `/api/${relationSlug}?${params.toString()}`,
          { credentials: "include", signal: ac.signal },
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as {
          docs?: Array<Record<string, unknown>>
        }
        const docs = (data.docs ?? []).map((d) => ({
          id: d.id as string | number,
          title: extractTitle(d),
          relationTo: relationSlug,
        }))
        setResults(docs)
        // Seed the title map with newly fetched docs.
        setTitles((prev) => {
          const next = new Map(prev)
          for (const d of docs) next.set(d.id, d.title)
          return next
        })
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.warn(
            `[shadcn-relationship] ${relationSlug} search failed:`,
            err,
          )
        }
      } finally {
        if (!ac.signal.aborted) setLoading(false)
      }
    }, 200)
    return () => {
      clearTimeout(t)
      ac.abort()
    }
  }, [open, query, relationSlug])

  const handleSelect = (id: string | number) => {
    if (hasMany) {
      const current = new Set(selectedIds)
      if (current.has(id)) current.delete(id)
      else current.add(id)
      setValue(Array.from(current) as FieldValue)
    } else {
      setValue(id as FieldValue)
      setOpen(false)
    }
  }

  const handleClear = () => setValue(null as FieldValue)

  // Fallback if the user configured a polymorphic relationship — bail
  // out to Payload's own component by returning `undefined`-like markup
  // with a hint. (applyShadcnFields skips polymorphic fields so this
  // shouldn't normally trigger.)
  if (!relationSlug) {
    return (
      <FieldShell label={label} required={required} error={errorMessage} showError={showError}>
        <p className="text-xs text-muted-foreground italic">
          Champ relation polymorphique — rendu par Payload.
        </p>
      </FieldShell>
    )
  }

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
      htmlFor={triggerId}
    >
      {/* Selected chips (hasMany) */}
      {hasMany && selectedIds.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {selectedIds.map((id) => (
            <span
              key={String(id)}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[12px] font-medium"
            >
              <span className="max-w-[180px] truncate">
                {titles.get(id) ?? `#${id}`}
              </span>
              <button
                type="button"
                onClick={() => handleSelect(id)}
                aria-label={`Retirer ${titles.get(id) ?? id}`}
                className="text-muted-foreground hover:text-destructive transition-colors"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={triggerId}
            type="button"
            variant="outline"
            aria-invalid={showError ? true : undefined}
            aria-expanded={open}
            className={cn(
              "w-full h-9 justify-between rounded-xl font-normal",
              !hasMany && selectedIds.length > 0
                ? "text-foreground"
                : "text-muted-foreground",
            )}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {hasMany
                  ? selectedIds.length > 0
                    ? `Ajouter — ${selectedIds.length} sélectionné${selectedIds.length > 1 ? "s" : ""}`
                    : `Choisir un ou plusieurs ${label.toLowerCase()}`
                  : selectedIds.length > 0
                    ? (titles.get(selectedIds[0]) ?? `#${selectedIds[0]}`)
                    : `Choisir un ${label.toLowerCase()}`}
              </span>
            </span>
            {!hasMany && selectedIds.length > 0 ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Effacer"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleClear()
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    handleClear()
                  }
                }}
                className="ml-2 inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
              >
                <X className="size-3.5" />
              </span>
            ) : (
              <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-60" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(420px,calc(100vw-2rem))] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Rechercher dans ${relationSlug}…`}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" />
                  <span>Recherche…</span>
                </div>
              ) : (
                <>
                  <CommandEmpty>Aucun résultat.</CommandEmpty>
                  <CommandGroup>
                    {results.map((doc) => {
                      const isSelected = selectedIds.includes(doc.id)
                      return (
                        <CommandItem
                          key={String(doc.id)}
                          value={String(doc.id)}
                          onSelect={() => handleSelect(doc.id)}
                          className="gap-2"
                        >
                          <Check
                            className={cn(
                              "size-4 shrink-0",
                              isSelected ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <span className="flex-1 truncate">{doc.title}</span>
                          <span className="text-[11px] text-muted-foreground tabular-nums">
                            #{doc.id}
                          </span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FieldShell>
  )
}

export default ShadcnRelationshipField
